import { openDB, type IDBPDatabase } from 'idb';
import type { StoredImage, StoredAudio } from '@/types';
import { INDEXED_DB_NAME, INDEXED_DB_VERSION, INDEXED_DB_STORES } from '@/lib/constants';
import { logger } from '@/lib/logger';

export class IndexedDBAdapter {
  private db: IDBPDatabase | null = null;

  async init(): Promise<void> {
    try {
      this.db = await openDB(INDEXED_DB_NAME, INDEXED_DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(INDEXED_DB_STORES.IMAGES)) {
            db.createObjectStore(INDEXED_DB_STORES.IMAGES, { keyPath: 'sceneId' });
          }
          if (!db.objectStoreNames.contains(INDEXED_DB_STORES.AUDIOS)) {
            db.createObjectStore(INDEXED_DB_STORES.AUDIOS, { keyPath: 'sceneId' });
          }
        },
      });
    } catch (error) {
      logger.error('IndexedDBの初期化に失敗しました', error as Error);
      throw new Error('IndexedDBの初期化に失敗しました');
    }
  }

  private ensureDB(): IDBPDatabase {
    if (!this.db) {
      throw new Error('IndexedDB is not initialized. Call init() first.');
    }
    return this.db;
  }

  async saveImage(sceneId: string, blob: Blob): Promise<string> {
    const db = this.ensureDB();
    const imageData: StoredImage = {
      sceneId,
      blob,
      mimeType: blob.type,
    };
    await db.put(INDEXED_DB_STORES.IMAGES, imageData);
    return URL.createObjectURL(blob);
  }

  async getImage(sceneId: string): Promise<string | null> {
    const db = this.ensureDB();
    const data = await db.get(INDEXED_DB_STORES.IMAGES, sceneId);
    return data ? URL.createObjectURL(data.blob) : null;
  }

  async deleteImage(sceneId: string): Promise<void> {
    const db = this.ensureDB();
    await db.delete(INDEXED_DB_STORES.IMAGES, sceneId);
  }

  async saveAudio(sceneId: string, blob: Blob, duration: number): Promise<string> {
    const db = this.ensureDB();
    const audioData: StoredAudio = {
      sceneId,
      blob,
      duration,
    };
    await db.put(INDEXED_DB_STORES.AUDIOS, audioData);
    return URL.createObjectURL(blob);
  }

  async getAudio(sceneId: string): Promise<{ url: string; duration: number } | null> {
    const db = this.ensureDB();
    const data = await db.get(INDEXED_DB_STORES.AUDIOS, sceneId);
    return data ? { url: URL.createObjectURL(data.blob), duration: data.duration } : null;
  }

  async deleteAudio(sceneId: string): Promise<void> {
    const db = this.ensureDB();
    await db.delete(INDEXED_DB_STORES.AUDIOS, sceneId);
  }

  async deleteSceneData(sceneId: string): Promise<void> {
    await Promise.all([this.deleteImage(sceneId), this.deleteAudio(sceneId)]);
  }

  async clear(): Promise<void> {
    const db = this.ensureDB();
    await Promise.all([
      db.clear(INDEXED_DB_STORES.IMAGES),
      db.clear(INDEXED_DB_STORES.AUDIOS),
    ]);
  }
}

export const indexedDBAdapter = new IndexedDBAdapter();
